import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { useTimeclockStore } from '../../storeTimeclock';
import { useUserStore, useSubscriptionStore } from '../../store';
import { getCsrfToken } from '../../utils';

import { can } from '../../utils/access';
const NUMERIC = ['max_hours', 'auto_stop_grace_minutes'];

/**
 * Org-level time-clock settings (§13). The master switch toggles the org `time_clock_enabled`
 * feature flag (live timer system on/off); when off, the org uses manual work-time entry only and
 * the detailed §13 config is hidden. The config section is wired to GET/PATCH /timeclock/config
 * (admin-only writes, enforced server-side; the endpoint is itself gated on the flag). Save/Cancel
 * (no save-on-blur). Dropped settings (global auto_stop_time, auto_waiting_on_clockout) are absent.
 */
export default function TimeclockSettingsTab() {
  const { t } = useTranslation();
  const currentOrganization = useUserStore((s) => s.currentOrganization);
  const fetchUser = useUserStore((s) => s.fetchUser);
  const config = useTimeclockStore((s) => s.config);
  const fetchConfig = useTimeclockStore((s) => s.fetchConfig);
  const updateConfig = useTimeclockStore((s) => s.updateConfig);
  const subscriptionInfo = useSubscriptionStore((s) => s.subscriptionInfo);
  const fetchSubscriptionInfo = useSubscriptionStore((s) => s.fetchSubscriptionInfo);
  const canUseSms = useSubscriptionStore((s) => s.canUseSms);

  const enabled = !!currentOrganization?.time_clock_enabled;
  const orgId = currentOrganization?.organization_id;
  const isAdmin = can(currentOrganization, 'timeclock.settings');
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingMaster, setTogglingMaster] = useState(false);
  const [snack, setSnack] = useState(null);

  // Only load the detailed config once the feature is on (its endpoint 404s while off).
  useEffect(() => {
    if (!enabled) return;
    if (config) setForm({ ...config });
    else fetchConfig();
  }, [enabled, config, fetchConfig]);

  // Subscription info drives whether SMS reminder options are selectable.
  useEffect(() => {
    fetchSubscriptionInfo();
  }, [fetchSubscriptionInfo]);

  const toggleMaster = async (next) => {
    setTogglingMaster(true);
    try {
      await axios.patch(
        `/api/organizations/${orgId}/`,
        { time_clock_enabled: next },
        { headers: { 'X-CSRFToken': getCsrfToken(), 'X-Organization-Id': orgId } },
      );
      await fetchUser(); // refresh feature gates (nav, routes, pill) immediately
      if (next) fetchConfig();
      setSnack({ severity: 'success', message: t('worktime.settings.saved') });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.response?.data?.detail || t('worktime.error.generic') });
    } finally {
      setTogglingMaster(false);
    }
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      [
        'track', 'handling', 'allow_parallel', 'auto_start_in_progress', 'auto_pause_on_waiting',
        'auto_waiting_on_clock_stop',
        'max_hours', 'auto_stop_enabled', 'auto_stop_grace_minutes', 'break_counts_as_work_time',
        'remind_clockout', 'remind_confirm', 'reminder_channel',
      ].forEach((k) => {
        patch[k] = NUMERIC.includes(k) ? Number(form[k]) || 0 : form[k];
      });
      await updateConfig(patch);
      setSnack({ severity: 'success', message: t('worktime.settings.saved') });
    } catch (e) {
      setSnack({ severity: 'error', message: e?.response?.data?.detail || t('worktime.error.generic') });
    } finally {
      setSaving(false);
    }
  };

  const Group = ({ title, desc, children }) => (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        {desc && <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.25 }}>{desc}</Typography>}
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>{children}</Stack>
      </Box>
    </Paper>
  );

  const Segmented = ({ value, onChange, options }) => (
    <ToggleButtonGroup exclusive value={value} onChange={(e, v) => v && onChange(v)} size="small" fullWidth>
      {options.map((o) => (
        <ToggleButton key={o.value} value={o.value}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const toggle = (key, label, desc) => (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', m: 0 }}
      control={<Switch checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} />}
      label={
        <Box>
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
          {desc && <Typography sx={{ fontSize: 12, color: '#64748b' }}>{desc}</Typography>}
        </Box>
      }
    />
  );

  const numberField = (key, label, suffix) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        type="number"
        size="small"
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        sx={{ width: 100 }}
      />
      <Typography sx={{ fontSize: 14 }}>{label}</Typography>
      {suffix && <Typography sx={{ fontSize: 13, color: '#64748b' }}>{suffix}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('worktime.settings.title')}
      </Typography>
      <Typography sx={{ color: '#64748b', mb: 2 }}>{t('worktime.settings.subtitle')}</Typography>

      {/* Master switch — turns the whole live timer system on/off for this org. */}
      <Group title={t('worktime.settings.masterTitle')} desc={t('worktime.settings.masterDesc')}>
        <FormControlLabel
          sx={{ alignItems: 'flex-start', m: 0 }}
          control={
            <Switch
              checked={enabled}
              disabled={togglingMaster || !isAdmin}
              onChange={(e) => toggleMaster(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: 14 }}>{t('worktime.settings.masterToggle')}</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                {enabled ? t('worktime.settings.masterOn') : t('worktime.settings.masterOff')}
              </Typography>
            </Box>
          }
        />
      </Group>

      {!enabled && (
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>{t('worktime.settings.disabledHint')}</Typography>
      )}

      {enabled && !form && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {enabled && form && (
       <>
      <Group title={t('worktime.settings.trackTitle')} desc={t('worktime.settings.trackDesc')}>
        <Segmented
          value={form.track}
          onChange={(v) => set('track', v)}
          options={[
            { value: 'day', label: t('worktime.settings.trackDay') },
            { value: 'work_order', label: t('worktime.settings.trackWo') },
            { value: 'both', label: t('worktime.settings.trackBoth') },
          ]}
        />
      </Group>

      <Group title={t('worktime.settings.stateTitle')} desc={t('worktime.settings.stateDesc')}>
        {toggle('auto_start_in_progress', t('worktime.settings.autoStart'), t('worktime.settings.autoStartDesc'))}
        {toggle('auto_pause_on_waiting', t('worktime.settings.autoPause'), t('worktime.settings.autoPauseDesc'))}
        {toggle('auto_waiting_on_clock_stop', t('worktime.settings.autoWaiting'), t('worktime.settings.autoWaitingDesc'))}
      </Group>

      <Group title={t('worktime.settings.parallelTitle')} desc={t('worktime.settings.parallelDesc')}>
        {toggle('allow_parallel', t('worktime.settings.allowParallel'), t('worktime.settings.allowParallelDesc'))}
      </Group>

      <Group title={t('worktime.settings.guardTitle')} desc={t('worktime.settings.guardDesc')}>
        {toggle('auto_stop_enabled', t('worktime.settings.autoStop'), t('worktime.settings.autoStopDesc'))}
        {numberField('auto_stop_grace_minutes', t('worktime.settings.grace'), t('worktime.settings.minutes'))}
        {numberField('max_hours', t('worktime.settings.maxHours'), t('worktime.settings.hours'))}
      </Group>

      <Group title={t('worktime.settings.breakTitle')} desc={t('worktime.settings.breakDesc')}>
        {toggle('break_counts_as_work_time', t('worktime.settings.breakPaid'), t('worktime.settings.breakPaidDesc'))}
      </Group>

      <Group title={t('worktime.settings.handlingTitle')} desc={t('worktime.settings.handlingDesc')}>
        <Segmented
          value={form.handling}
          onChange={(v) => set('handling', v)}
          options={[
            { value: 'auto', label: t('worktime.settings.handlingAuto') },
            { value: 'confirm', label: t('worktime.settings.handlingConfirm') },
          ]}
        />
      </Group>

      <Group title={t('worktime.settings.remindTitle')} desc={t('worktime.settings.remindDesc')}>
        {toggle('remind_clockout', t('worktime.settings.remindClockout'))}
        {toggle('remind_confirm', t('worktime.settings.remindConfirm'))}

        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 1 }}>
            {t('worktime.settings.remindChannelLabel')}
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={form.reminder_channel || 'email'}
              onChange={(e) => set('reminder_channel', e.target.value)}
            >
              <Tooltip title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''} placement="right">
                <FormControlLabel
                  value="both"
                  control={<Radio size="small" disabled={!smsEnabled} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 14, color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                        {t('worktime.settings.channel_both')}
                      </Typography>
                      {!smsEnabled && isTrial && <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                    </Box>
                  }
                />
              </Tooltip>
              <FormControlLabel
                value="email"
                control={<Radio size="small" />}
                label={<Typography sx={{ fontSize: 14 }}>{t('worktime.settings.channel_email')}</Typography>}
              />
              <Tooltip title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''} placement="right">
                <FormControlLabel
                  value="sms"
                  control={<Radio size="small" disabled={!smsEnabled} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 14, color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                        {t('worktime.settings.channel_sms')}
                      </Typography>
                      {!smsEnabled && isTrial && <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                    </Box>
                  }
                />
              </Tooltip>
            </RadioGroup>
          </FormControl>
          {!smsEnabled && isTrial && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
            </Alert>
          )}
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('worktime.settings.smsCostInfo')}
          </Alert>
        </Box>
      </Group>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => setForm({ ...config })} disabled={saving}>
          {t('worktime.settings.cancel')}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {t('worktime.settings.save')}
        </Button>
      </Box>
       </>
      )}

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack?.severity || 'info'} variant="filled" onClose={() => setSnack(null)} sx={{ width: '100%' }}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
